import { useEffect } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0TagStatus } from '@factorialco/f0-react'
import { AtsOfferLetterTemplateSuccessPreviewDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { PdfViewer } from 'modules/ats/components/PdfViewer'

interface Props {
  onClose: () => void
  id: number
}

export const OfferLetterTemplateSuccessPreviewModal = ({ onClose, id }: Props) => {
  const { data, stopPolling } = useQuery(AtsOfferLetterTemplateSuccessPreviewDocument, {
    variables: { id },
    pollInterval: 1000,
    fetchPolicy: 'network-only',
  })

  const previewUrl = data?.ats?.offerLetterTemplate?.atsFile?.preview?.url

  useEffect(() => {
    if (previewUrl) {
      stopPolling()
    }
  }, [previewUrl, stopPolling])

  const pdfFilename = data?.ats?.offerLetterTemplate?.atsFile?.preview?.filename ?? 'preview.pdf'
  const isPreviewLoading = !previewUrl

  const systemVariables =
    data?.ats?.offerLetterTemplate?.systemVariables.map((variable) => ({
      name: variable.name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      value: `{{${variable.name}}}`,
      status: 'match',
    })) ?? []

  const customVariables =
    data?.ats?.offerLetterTemplate?.customVariables.map((variable) => ({
      name: variable.name,
      value: `{{${variable.name}}}`,
      status: 'custom',
    })) ?? []

  const recognizedVariables = [...systemVariables, ...customVariables]

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      position="fullscreen"
      title={data?.ats?.offerLetterTemplate?.name}
      primaryAction={{
        label: i18n.t('common.done'),
        onClick: onClose,
      }}
    >
      <Box height="full">
        <Box flexGrow flexDirection="row" overflowY="hidden">
          <Box flexGrow height="full" overflowY="auto">
            {isPreviewLoading ? (
              <Box padding="s40" width="full" alignItems="center" flexGrow>
                <Spinner />
              </Box>
            ) : (
              <PdfViewer url={previewUrl} filename={pdfFilename} initialScale="page-width" />
            )}
          </Box>
          <Box
            flexNoShrink
            height="full"
            overflowX="hidden"
            border={{ left: { style: 'solid', width: 's1', color: 'grey200' } }}
            style={{ width: '36%' }}
            paddingY="s24"
            paddingX="s16"
            gap="s16"
          >
            <Text.Normal size="300" color="textPrimary" weight="semibold">
              {i18n.t(
                'ats.settings.templates.offer_letter_templates.success_preview_modal.recognized_variables',
                {
                  num: recognizedVariables.length,
                }
              )}
            </Text.Normal>
            <Box
              borderRadius={{ all: 'abs016' }}
              width="full"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              {recognizedVariables.map((variable, index) => (
                <Box
                  key={`variable=${index}`}
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  gap="s16"
                  width="full"
                  paddingY="s12"
                  paddingLeft="s16"
                  paddingRight="s24"
                  border={
                    index === recognizedVariables.length - 1
                      ? undefined
                      : {
                          bottom: {
                            width: 's1',
                            style: 'solid',
                            color: 'grey400',
                          },
                        }
                  }
                >
                  <Box gap="s2">
                    <Text.Normal size="200" color="textPrimary" weight="medium">
                      {variable.name}
                    </Text.Normal>
                    <Box flexDirection="row" gap="s4">
                      <Text.Normal size="200">{variable.value}</Text.Normal>
                    </Box>
                  </Box>
                  <Box flexDirection="row" gap="s6">
                    <F0TagStatus
                      text={
                        variable.status === 'match'
                          ? i18n.t(
                              'ats.settings.templates.offer_letter_templates.success_preview_modal.match'
                            )
                          : i18n.t(
                              'ats.settings.templates.offer_letter_templates.success_preview_modal.custom'
                            )
                      }
                      variant={variable.status === 'match' ? 'positive' : 'info'}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}
